import { useEffect, useState } from 'react';
import { api, ApiCallError, type ParentChallenge } from '../api/client';
import { formatMoney, formatRelativeTime } from '@xiaopeiban/shared';
import type { ChildSummary } from '@xiaopeiban/shared';

// Values in sen (RM × 100); UI formatMoney renders them as RM X.XX.
const TEMPLATES: Array<{ icon: string; title: string; description: string; cost: number; reward: number }> = [
  { icon: '🌨️', title: '冬天来了',     description: '给动物们买暖垫', cost: 3000, reward: 500 },
  { icon: '💧', title: '水系统老化',   description: '升级水瓶和水管', cost: 5000, reward: 1000 },
  { icon: '🐛', title: '有虫子来了',   description: '买清洁用品打扫场馆', cost: 2000, reward: 500 },
  { icon: '🎉', title: '周末聚会',     description: '多准备点食物招待', cost: 4000, reward: 1500 },
  { icon: '🏥', title: '小仓鼠生病了', description: '买药给它', cost: 3500, reward: 1000 },
  { icon: '🎂', title: '宠物生日',     description: '买生日蛋糕给它', cost: 2000, reward: 2000 },
  { icon: '📚', title: '这周好好学习', description: '完成 20 道题', cost: 0, reward: 3000 },
  { icon: '🧹', title: '整理你的房间', description: '让房间焕然一新', cost: 0, reward: 2000 },
];

export default function ChallengesPage() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [childId, setChildId] = useState<number | null>(null);
  const [challenges, setChallenges] = useState<ParentChallenge[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [icon, setIcon] = useState('⭐');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [reward, setReward] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const c = await api.listChildren();
      setChildren(c.children);
      if (c.children.length > 0) {
        const id = childId ?? c.children[0].id;
        setChildId(id);
        const ch = await api.listChallenges(id);
        setChallenges(ch.challenges);
      }
    } catch (e) { setErr(niceError(e)); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (childId == null) return;
    api.listChallenges(childId).then(r => setChallenges(r.challenges)).catch(e => setErr(niceError(e)));
  }, [childId]);

  function useTemplate(t: typeof TEMPLATES[number]) {
    setIcon(t.icon); setTitle(t.title); setDescription(t.description);
    // Templates hold sen; fill the input as RM (divide by 100)
    setCost(t.cost ? (t.cost / 100).toString() : '');
    setReward(t.reward ? (t.reward / 100).toString() : '');
    setShowNew(true);
  }

  async function save() {
    if (childId == null) return;
    if (!title.trim()) { setErr('请填标题'); return; }
    const cRm = cost ? parseFloat(cost) : 0;
    const rRm = reward ? parseFloat(reward) : 0;
    if (!Number.isFinite(cRm) || cRm < 0) { setErr('cost 不对'); return; }
    if (!Number.isFinite(rRm) || rRm < 0) { setErr('reward 不对'); return; }
    const cSen = Math.round(cRm * 100);
    const rSen = Math.round(rRm * 100);
    setSaving(true);
    try {
      await api.createChallenge({
        child_id: childId, title: title.trim(),
        description: description.trim() || undefined,
        icon, cost: cSen, reward_points: rSen,
      });
      setShowNew(false);
      setTitle(''); setDescription(''); setCost(''); setReward(''); setIcon('⭐');
      const ch = await api.listChallenges(childId);
      setChallenges(ch.challenges);
    } catch (e) { setErr(niceError(e)); }
    finally { setSaving(false); }
  }

  async function remove(c: ParentChallenge) {
    if (!confirm(`确定删除"${c.title}"？`)) return;
    try {
      await api.deleteChallenge(c.id);
      if (childId) {
        const r = await api.listChallenges(childId);
        setChallenges(r.challenges);
      }
    } catch (e) { setErr(niceError(e)); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">🎯 给孩子出挑战</div>
        <div className="page-subtitle">真实生活给游戏里的动物世界加点意外</div>
      </div>

      {err && <div className="error-box">{err}</div>}

      <div className="card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>💡 这个怎么玩？</div>
        <div style={{ fontSize: 14, color: '#1e3a8a', lineHeight: 1.6 }}>
          你可以随时给孩子"派任务"，比如"冬天来了，给动物买暖垫 RM 30"。<br/>
          孩子首页会看到这个挑战，选择处理（花钱/获得奖励）或忽略。教她真实场景里怎么应对突发开销。
        </div>
      </div>

      {children.length > 1 && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>选择孩子</div>
          <div className="flex" style={{ flexWrap: 'wrap' }}>
            {children.map(c => (
              <button
                key={c.id}
                className={`btn ${c.id === childId ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setChildId(c.id)}
              >
                {c.avatar || '👶'} {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 700 }}>挑战列表（{challenges.length}）</div>
          <button className="btn btn-primary" onClick={() => setShowNew(!showNew)}>
            {showNew ? '取消' : '+ 新建'}
          </button>
        </div>

        {showNew && (
          <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 12, marginBottom: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10 }}>
              <div>
                <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 4 }}>图标</label>
                <input
                  value={icon}
                  onChange={e => setIcon(e.target.value)}
                  maxLength={2}
                  style={{ width: 70, textAlign: 'center', fontSize: 22 }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 4 }}>标题</label>
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="例如 冬天来了" />
              </div>
            </div>

            <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginTop: 10, marginBottom: 4 }}>说明（可选）</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="给动物们买暖垫" />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <div>
                <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 4 }}>💰 花费 RM</label>
                <input type="number" step="0.01" value={cost} onChange={e => setCost(e.target.value)} placeholder="0=免费" />
              </div>
              <div>
                <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 4 }}>🎁 奖励 RM</label>
                <input type="number" step="0.01" value={reward} onChange={e => setReward(e.target.value)} placeholder="0=无奖励" />
              </div>
            </div>

            <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} disabled={saving} onClick={save}>
              {saving ? <span className="spinner" /> : '发给孩子'}
            </button>
          </div>
        )}

        {challenges.length === 0 && !showNew && (
          <div className="muted" style={{ textAlign: 'center', padding: 16 }}>
            还没有挑战，试试下面模板 ↓
          </div>
        )}

        {challenges.map(c => (
          <div key={c.id} style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
            <div className="flex" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700 }}>
                  <span style={{ fontSize: 22, marginRight: 6 }}>{c.icon}</span>{c.title}
                  {c.resolved_at && <span className="tag" style={{ marginLeft: 6, background: '#dcfce7', color: '#166534' }}>已完成</span>}
                </div>
                {c.description && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{c.description}</div>}
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {c.cost > 0 && `花 ${formatMoney(c.cost)}`}
                  {c.cost > 0 && c.reward_points > 0 && ' · '}
                  {c.reward_points > 0 && `奖 ${formatMoney(c.reward_points)}`}
                  {' · '}{formatRelativeTime(c.created_at)}
                </div>
              </div>
              {!c.resolved_at && (
                <button className="btn btn-danger" onClick={() => remove(c)}>删除</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div style={{ fontWeight: 700, marginBottom: 10 }}>💡 常用模板</div>
        {TEMPLATES.map((t, i) => (
          <div
            key={i}
            onClick={() => useTemplate(t)}
            style={{
              padding: 10, border: '1px solid var(--border)', borderRadius: 12,
              marginBottom: 8, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <div style={{ fontSize: 26 }}>{t.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{t.title}</div>
              <div className="muted" style={{ fontSize: 13 }}>{t.description}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                花 {formatMoney(t.cost)}
                {t.reward > 0 && ` · 奖 ${formatMoney(t.reward)}`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || '出错了';
}
